import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/app.css';

/**
 * Точка входа клиента. Одна и та же для трёх оболочек: Tauri на Windows,
 * Capacitor на iOS и Android, браузер как PWA. Различия платформ спрятаны за
 * PlatformAdapter, а не разбросаны по коду интерфейса.
 *
 * Порядок запуска важен. Сессия создаётся до первой отрисовки, а локальная база
 * — уже внутри неё, после выбора клиники: у файлов базы имя с идентификатором
 * арендатора, и открыть их до входа значит перепутать клиники на общем
 * компьютере регистратуры.
 */

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('Не найден корневой элемент');

  const [{ resolvePlatform }, { registerServiceWorker }, { createSession }, { App }] =
    await Promise.all([
      import('./lib/platform/index.js'),
      import('./lib/platform/service-worker.js'),
      import('./lib/session/index.js'),
      import('./app/app.js'),
    ]);

  // Кэш оболочки заводится до входа и не зависит от него: перезагрузка планшета
  // без сети обязана открыть приложение, а не ошибку соединения. Регистрация
  // ничего не ждёт — первая отрисовка от неё не задерживается.
  registerServiceWorker();

  const adapter = await resolvePlatform();
  bindShellKeys(adapter.shellWindow);

  const session = await createSession({
    adapter,
    baseUrl: apiBaseUrl(),
    syncUrl: syncUrl(),
    billingUrl: billingBaseUrl(),
    notificationsUrl: notificationsBaseUrl(),
  });

  createRoot(container).render(
    <StrictMode>
      <App session={session} />
    </StrictMode>,
  );
}

/**
 * F11 — полный экран в оболочке, как в любой программе Windows. В браузере
 * клавишу обрабатывает сам браузер, и здесь ничего не подключается.
 */
function bindShellKeys(shellWindow: { toggleFullscreen(): Promise<void> } | undefined): void {
  if (shellWindow === undefined) return;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'F11' || event.repeat) return;
    event.preventDefault();
    void shellWindow.toggleFullscreen().catch(() => undefined);
  });
}

/**
 * Адрес API. В браузере по умолчанию тот же источник, что и страница: так
 * refresh-cookie остаётся первой стороной, и её не режут настройки приватности.
 * Разработка и нативные оболочки переопределяют адрес переменной сборки.
 */
function apiBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_BASE_URL;
  return typeof configured === 'string' && configured !== '' ? configured : window.location.origin;
}

/**
 * Адрес шлюза уведомлений. По умолчанию — тот же источник: страница по https не
 * вправе открыть соединение по ws, и вычислять протокол из адреса страницы
 * надёжнее, чем помнить про это в каждой сборке. В облаке шлюз стоит на своём
 * домене и задаётся переменной.
 *
 * Шлюз может быть недоступен или не запущен вовсе — свежесть кэша тогда
 * держится на опросе, и это рабочее состояние, а не отказ.
 */
/**
 * Адрес коммерческого контура. По умолчанию — тот же источник с префиксом
 * `/billing`: подписка ходит через тот же вход, что и всё остальное, и не
 * требует ни второго домена, ни отдельной настройки CORS.
 *
 * Биллинг может быть не поднят — экран подписки тогда покажет отказ, а работа
 * клиники не пострадает: приём пациентов от оплаты не зависит.
 */
function billingBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_BILLING_BASE_URL;
  return typeof configured === 'string' && configured !== ''
    ? configured
    : `${window.location.origin}/billing`;
}

/**
 * Адрес сервиса уведомлений. По умолчанию — тот же источник с префиксом
 * `/notifications`, тем же приёмом, что и у биллинга.
 *
 * Сервис может быть не поднят — экран журнала уведомлений тогда покажет
 * отказ, а приём пациентов не пострадает.
 */
function notificationsBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_NOTIFICATIONS_BASE_URL;
  return typeof configured === 'string' && configured !== ''
    ? configured
    : `${window.location.origin}/notifications`;
}

function syncUrl(): string {
  const configured: unknown = import.meta.env.VITE_SYNC_URL;
  const base =
    typeof configured === 'string' && configured !== ''
      ? configured
      : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

  return `${base.replace(/\/+$/, '')}/ws`;
}

void bootstrap();
